import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegFileExcel } from 'react-icons/fa';
import dayjs, { Dayjs } from 'dayjs';
import { Button, Card, Select, EmptyPlaceholder, exportToExcel, DatepickerV2 } from '@client/toolkit';
import {
  useGetAgreementsReportToInvoiceQuery,
  useGetInvoicesReceivableDetailsQuery,
  InvoiceStatus,
  AgreementsToInvoiceReportSort,
  SortOrder,
} from '@client/api';
import { InvoiceOverviewTable, SortDirection } from './invoice-overview-table.component';
import { AgreementsToInvoiceTable } from './agreements-to-invoice-table.component';
import { InvoiceOverviewItem, AgreementToInvoiceItem } from './mock-data';
import { DateRangePreset, DateRangePresetKey, useDefaultDateRangePresets } from '../../../../dashboard/components/DashboardFilters/useDefaultDateRangePresets';

const REPORTS_PRESETS = [
  DateRangePresetKey.THISMONTH,
  DateRangePresetKey.LASTMONTH,
  DateRangePresetKey.LAST6MONTHS,
  DateRangePresetKey.NEXT6MONTHS,
  DateRangePresetKey.NEXT12MONTHS,
  DateRangePresetKey.THISYEAR,
  DateRangePresetKey.LASTYEAR,
  DateRangePresetKey.NEXTYEAR,
  DateRangePresetKey.CUSTOM,
];

const PAGE_SIZE = 25;

const COLUMN_TO_SORT: Record<string, AgreementsToInvoiceReportSort> = {
  periodFrom: 'PeriodFrom',
  amount: 'Amount',
  tenantName: 'TenantName',
  agreementName: 'AgreementName',
};

type ReportType = 'invoice-overview' | 'agreements-to-invoice';

export function AgreementReports() {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation('agreements');

  const [selectedReport, setSelectedReport] = useState<ReportType>('agreements-to-invoice');
  const [invoiceSortColumn, setInvoiceSortColumn] = useState<string | undefined>('dueDate');
  const [invoiceSortDirection, setInvoiceSortDirection] = useState<SortDirection | undefined>('asc');
  const [sortBy, setSortBy] = useState<AgreementsToInvoiceReportSort>('PeriodFrom');
  const [sortOrder, setSortOrder] = useState<SortOrder>('Asc');
  const [offset, setOffset] = useState(0);

  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  const { presets, activePreset, setActivePreset } = useDefaultDateRangePresets({
    defaultPresetKey: DateRangePresetKey.THISMONTH,
    visiblePresets: REPORTS_PRESETS,
  });

  const {
    data: agreementsResponse,
    isLoading: isLoadingAgreements,
    isFetching: isFetchingAgreements,
    error: agreementsError,
  } = useGetAgreementsReportToInvoiceQuery(
    {
      periodFrom: dateRange[0]?.format('YYYY-MM-DD'),
      periodTo: dateRange[1]?.format('YYYY-MM-DD'),
      sortBy,
      sortOrder,
      'pagination.Offset': offset,
      'pagination.Limit': PAGE_SIZE,
    },
    { skip: selectedReport !== 'agreements-to-invoice' },
  );

  const {
    data: receivableResponse,
    isLoading: isLoadingReceivables,
    isFetching: isFetchingReceivables,
    error: receivablesError,
  } = useGetInvoicesReceivableDetailsQuery(
    {
      startDate: dateRange[0]?.format('YYYY-MM-DD'),
      endDate: dateRange[1]?.format('YYYY-MM-DD'),
      pageSize: 500,
    },
    { skip: selectedReport !== 'invoice-overview' || !dateRange[0] || !dateRange[1] },
  );

  const agreementsData: AgreementToInvoiceItem[] = useMemo(() => {
    if (!agreementsResponse?.items) return [];
    return agreementsResponse.items.map((item) => ({
      id: item.agreementId,
      agreementId: item.agreementId,
      agreementName: item.agreementName,
      tenantName: item.tenantName,
      periodFrom: item.periodFrom,
      periodTo: item.periodTo,
      amount: item.amount.amount,
      status: 'ready' as AgreementToInvoiceItem['status'],
    }));
  }, [agreementsResponse]);

  const handlePresetClick = useCallback(
    (preset: DateRangePreset) => {
      setActivePreset(preset);
      if (preset.from && preset.to) {
        setDateRange([preset.from, preset.to]);
        setOffset(0);
      }
    },
    [setActivePreset],
  );

  const handleFromDateChange = useCallback(
    (date: Dayjs | null) => {
      setDateRange((prev) => {
        const toReset =
          selectedReport === 'invoice-overview' && date && prev[1] && date.year() !== prev[1].year();
        return [date, toReset ? null : prev[1]];
      });
      setOffset(0);
    },
    [selectedReport],
  );

  const handleToDateChange = useCallback((date: Dayjs | null) => {
    setDateRange((prev) => [prev[0], date]);
    setOffset(0);
  }, []);

  const invoiceData: InvoiceOverviewItem[] = useMemo(() => {
    if (!receivableResponse?.items) return [];
    const seen = new Set<number>();
    const items: InvoiceOverviewItem[] = [];
    for (const building of receivableResponse.items) {
      for (const tenant of building.tenantReceivables) {
        for (const invoice of tenant.invoices) {
          if (seen.has(invoice.invoiceNumber)) continue;
          seen.add(invoice.invoiceNumber);
          items.push({
            id: invoice.invoiceId,
            tenantName: tenant.tenantName,
            invoiceNumber: invoice.invoiceNumber,
            invoiceDate: invoice.invoiceDate,
            dueDate: invoice.dueDate,
            amount: invoice.amount.value,
            amountOutstanding: invoice.amountOutstanding.value,
            currency: invoice.amountOutstanding.currency,
            status: invoice.status as InvoiceStatus,
          });
        }
      }
    }
    return items;
  }, [receivableResponse]);

  const sortedInvoiceData = useMemo(() => {
    if (!invoiceSortColumn || !invoiceSortDirection) return invoiceData;
    return [...invoiceData].sort((a, b) => {
      const aValue: any = a[invoiceSortColumn as keyof InvoiceOverviewItem];
      const bValue: any = b[invoiceSortColumn as keyof InvoiceOverviewItem];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return invoiceSortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return invoiceSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [invoiceData, invoiceSortColumn, invoiceSortDirection]);

  const handleInvoiceSort = useCallback((column: string, direction: SortDirection) => {
    setInvoiceSortColumn(column);
    setInvoiceSortDirection(direction);
  }, []);

  const handleAgreementsSort = useCallback((column: string, direction: SortDirection) => {
    setSortBy(COLUMN_TO_SORT[column] ?? 'PeriodFrom');
    setSortOrder(direction === 'desc' ? 'Desc' : 'Asc');
    setOffset(0);
  }, []);

  // FE-facing sort state derived from BE params (for the table header indicators)
  const agreementsSortColumn = Object.entries(COLUMN_TO_SORT).find(([, v]) => v === sortBy)?.[0];
  const agreementsSortDirection: SortDirection = sortOrder === 'Desc' ? 'desc' : 'asc';

  const handleExportExcel = useCallback(() => {
    if (selectedReport === 'invoice-overview') {
      if (sortedInvoiceData.length === 0) return;
      const exportData = sortedInvoiceData.map((item) => ({
        [t('reports.invoiceTable.tenant')]: item.tenantName,
        [t('reports.invoiceTable.invoiceNumber')]: item.invoiceNumber,
        [t('reports.invoiceTable.invoiceDate')]: item.invoiceDate,
        [t('reports.invoiceTable.dueDate')]: item.dueDate,
        [t('reports.invoiceTable.amount')]: item.amount,
        [t('reports.invoiceTable.outstanding')]: item.amountOutstanding,
        [t('reports.invoiceTable.status')]: t(`reports.invoiceStatus.${item.status}`),
      }));
      exportToExcel(exportData, 'fakturaoversikt.xlsx');
    } else {
      if (agreementsData.length === 0) return;
      const exportData = agreementsData.map((item) => ({
        [t('reports.agreementsTable.tenant')]: item.tenantName,
        [t('reports.agreementsTable.agreementName')]: item.agreementName,
        [t('reports.agreementsTable.periodFrom')]: item.periodFrom,
        [t('reports.agreementsTable.periodTo')]: item.periodTo,
        [t('reports.agreementsTable.amount')]: item.amount,
        [t('reports.agreementsTable.status')]: t('reports.agreementsTable.readyForInvoicing'),
      }));
      exportToExcel(exportData, 'avtaler-til-fakturering.xlsx');
    }
  }, [selectedReport, sortedInvoiceData, agreementsData, t]);

  const isLoading =
    selectedReport === 'agreements-to-invoice'
      ? isLoadingAgreements || isFetchingAgreements
      : isLoadingReceivables || isFetchingReceivables;

  const error =
    agreementsError && selectedReport === 'agreements-to-invoice'
      ? t('reports.error.fetchAgreements')
      : receivablesError && selectedReport === 'invoice-overview'
        ? t('reports.error.fetchInvoices')
        : null;

  const totalCount = agreementsResponse?.totalCount ?? 0;
  const hasPrevious = agreementsResponse?.hasPrevious ?? false;
  const hasNext = agreementsResponse?.hasNext ?? false;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="w-12 h-12 border-2 border-brand-primary rounded-full animate-spin border-r-transparent" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="px-4 py-8">
          <EmptyPlaceholder>{error}</EmptyPlaceholder>
        </div>
      );
    }

    if (selectedReport === 'invoice-overview') {
      if (sortedInvoiceData.length === 0) {
        return (
          <div className="px-4 py-8">
            <EmptyPlaceholder>{t('reports.noResults')}</EmptyPlaceholder>
          </div>
        );
      }
      return (
        <InvoiceOverviewTable
          data={sortedInvoiceData}
          sortColumn={invoiceSortColumn}
          sortDirection={invoiceSortDirection}
          onSort={handleInvoiceSort}
        />
      );
    }

    if (selectedReport === 'agreements-to-invoice') {
      if (agreementsData.length === 0) {
        return (
          <div className="px-4 py-8">
            <EmptyPlaceholder>{t('reports.noAgreementsWithUpcomingPeriod')}</EmptyPlaceholder>
          </div>
        );
      }
      return (
        <>
          <AgreementsToInvoiceTable
            data={agreementsData}
            sortColumn={agreementsSortColumn}
            sortDirection={agreementsSortDirection}
            onSort={handleAgreementsSort}
          />
          {totalCount > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 pb-4">
              <span className="text-sm text-gray-400">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)} / {totalCount}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  color="primary-light"
                  disabled={!hasPrevious}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                >
                  {tCommon('pagination.previous', 'Previous')}
                </Button>
                <Button
                  size="sm"
                  color="primary-light"
                  disabled={!hasNext}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                >
                  {tCommon('pagination.next', 'Next')}
                </Button>
              </div>
            </div>
          )}
        </>
      );
    }

    return null;
  };

  const hasData =
    selectedReport === 'invoice-overview' ? sortedInvoiceData.length > 0 : agreementsData.length > 0;

  return (
    <Card className="mb-4">
      <div className="flex flex-col px-4 py-4 gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-white font-medium whitespace-nowrap">{t('reports.title')}</span>
          <Select
            value={selectedReport}
            onChange={(e) => setSelectedReport(e.target.value as ReportType)}
            className="w-64 flex-shrink-0"
          >
            <option value="invoice-overview">{t('reports.invoiceOverview')}</option>
            <option value="agreements-to-invoice">{t('reports.agreementsToInvoice')}</option>
          </Select>
        </div>
        <div className="inline-flex flex-wrap gap-2">
          {presets.map((preset) => {
            const crossesYear =
              selectedReport === 'invoice-overview' &&
              preset.from !== null &&
              preset.to !== null &&
              preset.from.year() !== preset.to.year();
            return (
              <Button
                key={preset.key}
                size="sm"
                color={activePreset?.key === preset.key ? 'primary' : 'primary-light'}
                onClick={() => handlePresetClick(preset)}
                disabled={crossesYear}
              >
                {preset.label}
              </Button>
            );
          })}
        </div>
        {activePreset?.key === DateRangePresetKey.CUSTOM && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-400">{t('reports.dateFrom')}</span>
            <DatepickerV2 value={dateRange[0]} onChange={handleFromDateChange} className="w-40" />
            <span className="text-sm text-gray-400">{t('reports.dateTo')}</span>
            <DatepickerV2
              value={dateRange[1]}
              onChange={handleToDateChange}
              className="w-40"
              minDate={
                selectedReport === 'invoice-overview' && dateRange[0]
                  ? dateRange[0].startOf('year')
                  : undefined
              }
              maxDate={
                selectedReport === 'invoice-overview' && dateRange[0]
                  ? dateRange[0].endOf('year')
                  : undefined
              }
            />
          </div>
        )}
        {hasData && !isLoading && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              color="primary-light"
              size="sm"
              onClick={handleExportExcel}
              className="text-white font-normal whitespace-nowrap flex-shrink-0"
              startIcon={<FaRegFileExcel className="text-brand-primary" />}
            >
              {t('reports.exportExcel')}
            </Button>
          </div>
        )}
      </div>
      {renderContent()}
    </Card>
  );
}
